import { theme } from '@Guidelines/Theme/theme';
import useCanEditDocument from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useCanEditDocument';
import FieldLinkCard from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/Shared/FieldLinkCard';
import type { DocumentPanelTarget } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/documentPanelSignal';
import type DocumentFieldView from '@UI/Store/Documents/Views/DocumentFieldView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

const panelStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const sentenceStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	line-height: 1.6;
	color: ${theme.colors.inkSecondary};
`;

type Props = {
	readonly target: Extract<DocumentPanelTarget, { readonly type: 'condition' }>;
};

/**
 * The values behind a piece of text that changes with them, one card per field. A single value never
 * reaches this panel (it opens the field's own panel), so the cards are the choice between several.
 */
const ConditionPanel: React.FC<Props> = ({ target }) => {
	const document = useSelector((state) => state.documentsStore.document);
	const canEdit = useCanEditDocument();

	const fields = React.useMemo(
		() =>
			target.fieldKeys
				.map((fieldKey) => document?.findFieldByKey(fieldKey) ?? null)
				.filter((field): field is DocumentFieldView => field !== null),
		[document, target.fieldKeys],
	);

	return (
		<div css={panelStyle}>
			<ConditionalLoader
				data={fields.length > 0 ? fields : null}
				fallback={<p css={sentenceStyle}>Ce texte dépend du contenu du document.</p>}
			>
				{(shown) => (
					<React.Fragment>
						<p css={sentenceStyle}>
							{canEdit ? 'Modifiez la valeur qui convient.' : 'Valeurs concernées.'}
						</p>
						{shown.map((field) => (
							<FieldLinkCard
								key={field.key}
								field={field}
								actionLabel={canEdit ? 'Modifier' : 'Voir'}
							/>
						))}
					</React.Fragment>
				)}
			</ConditionalLoader>
		</div>
	);
};

export default ConditionPanel;
